import React, { useState } from 'react';
import { Row, Col, Form, Button, Table, Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faClock, faSave, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ISystemCodeByTypeDto } from '../../../../utils/constants/interfaces';
import { SortableHeader } from '../../../ui/SortableHeader/SortableHeader';
import ModalDialog from '../../../ui/ModalDialog/ModalDialog';
import {
  Helpers,
  getCurrentBillingYear,
  getWorkHoursPeriodStart,
  getWorkHoursPeriodEnd
} from '../../../../utils/helpers';
import { useWorkHours, WorkHoursOrderBy } from './useWorkHours';
import styles from './WorkHoursSection.module.css';

interface WorkHoursSectionProps {
  clubMemberId: string;
  committees: ISystemCodeByTypeDto[];
  loadingCommittees: boolean;
  fieldErrors: string[];
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>;
  memberFirstName?: string;
  memberLastName?: string;
  showHeader?: boolean;
}

export const WorkHoursSection: React.FC<WorkHoursSectionProps> = ({
  clubMemberId,
  committees,
  loadingCommittees,
  fieldErrors,
  setFieldErrors,
  memberFirstName,
  memberLastName,
  showHeader = false
}) => {
  const workHoursHook = useWorkHours({
    clubMemberId,
    fieldErrors,
    setFieldErrors,
    committees
  });

  const [showDeleteWorkHoursModal, setShowDeleteWorkHoursModal] = useState<boolean>(false);

  const {
    loadingWorkHours,
    savingWorkHours,
    workHoursFormData,
    workHoursFormMode,
    selectedWorkHoursRow,
    workHoursYearFilter,
    workHoursOrderBy,
    workHoursSortField,
    workHoursSortDirection,
    updateWorkHoursFormData,
    saveWorkHours,
    deleteWorkHours,
    clearWorkHoursForm,
    enableAddMode,
    enableEditMode,
    setWorkHoursYearFilter,
    setWorkHoursOrderBy,
    handleWorkHoursSort,
    getAvailableYears,
    getFilteredAndSortedWorkHours
  } = workHoursHook;

  const isNew = Helpers.isNullOrWhiteSpace(clubMemberId) || clubMemberId === 'new';

  if (isNew) {
    return (
      <div className={styles.placeholderContent}>
        <FontAwesomeIcon icon={faClock} className={styles.placeholderIcon} />
        <div className={styles.placeholderText}>Work Hours tracking available after save</div>
        <div className={styles.placeholderSubtext}>
          Save the member information first to start tracking work hours
        </div>
      </div>
    );
  }

  const availableYears = getAvailableYears();
  const filteredAndSortedWorkHours = getFilteredAndSortedWorkHours();

  // Get all work hours for totals calculation
  const allWorkHours = workHoursHook.workHours;

  // Helper function to determine row highlight class
  const getRowHighlightClass = (workHour: (typeof filteredAndSortedWorkHours)[0]): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const workHoursDate = new Date(workHour.workHoursDateUtc);
    workHoursDate.setHours(0, 0, 0, 0);

    const createdDate = new Date(workHour.dateCreatedUtc);
    createdDate.setHours(0, 0, 0, 0);

    const lastModifiedDate = new Date(workHour.lastModifiedDateUtc);
    lastModifiedDate.setHours(0, 0, 0, 0);

    // Check if work hours date is in the future (highest priority)
    if (workHoursDate > today) {
      return styles.futureDate;
    }

    // Check if entered today FIRST (before checking updated)
    // This handles the case where created and modified are the same
    if (createdDate.getTime() === today.getTime()) {
      // If created and modified are the same, it was just entered today
      if (createdDate.getTime() === lastModifiedDate.getTime()) {
        return styles.enteredToday;
      }
      // If they're different, it was created AND updated today - show as updated
      return styles.updatedToday;
    }

    // Check if updated today (but not created today)
    if (lastModifiedDate.getTime() === today.getTime()) {
      return styles.updatedToday;
    }

    return '';
  };

  return (
    <>
      {/* Header with totals (only show when requested) */}
      {showHeader && memberFirstName && memberLastName && (
        <div className="d-flex align-items-center mb-4 mx-3">
          <h3 className="mb-0 me-5">
            Work Hours for: {memberFirstName} {memberLastName}
          </h3>
          {!loadingWorkHours && allWorkHours.length > 0 && (
            <div>
              {/* Totals are shown exactly as recorded (half-hour granularity) — no
                  rounding, so a stray fractional entry can't inflate the total to
                  the next whole hour. */}
              <div className="mb-1 text-success fw-bold">
                Total Hours worked in {getCurrentBillingYear()}:{' '}
                {allWorkHours
                  .filter((workHour) => {
                    const workDate = new Date(workHour.workHoursDateUtc);
                    const billingYear = getCurrentBillingYear();
                    const periodStart: Date = getWorkHoursPeriodStart(billingYear);
                    const periodEnd: Date = getWorkHoursPeriodEnd(billingYear);
                    return workDate >= periodStart && workDate <= periodEnd;
                  })
                  .reduce((sum, workHour) => sum + workHour.workHoursAmount, 0)
                  .toFixed(2)}
              </div>
              <div className="text-success fw-bold">
                Total Hours worked in {getCurrentBillingYear() - 1}:{' '}
                {allWorkHours
                  .filter((workHour) => {
                    const workDate = new Date(workHour.workHoursDateUtc);
                    const billingYear = getCurrentBillingYear() - 1;
                    const periodStart: Date = getWorkHoursPeriodStart(billingYear);
                    const periodEnd: Date = getWorkHoursPeriodEnd(billingYear);
                    return workDate >= periodStart && workDate <= periodEnd;
                  })
                  .reduce((sum, workHour) => sum + workHour.workHoursAmount, 0)
                  .toFixed(2)}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Add Work Hours Form */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon
              icon={workHoursFormMode === 'edit' ? faEdit : faPlus}
              className="me-2"
            />
            {workHoursFormMode === 'edit' ? 'Edit Work Hours' : 'Add Work Hours'}
          </h5>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={workHoursFormData.workHoursDateUtc}
                    onChange={(e) => updateWorkHoursFormData('workHoursDateUtc', e.target.value)}
                    isInvalid={fieldErrors.includes('workhours:workHoursDateUtc')}
                    disabled={workHoursFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">Date is required.</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Hours</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.5"
                    min="0"
                    value={workHoursFormData.workHoursAmount}
                    onChange={(e) => updateWorkHoursFormData('workHoursAmount', e.target.value)}
                    isInvalid={fieldErrors.includes('workhours:workHoursAmount')}
                    placeholder="0.0"
                    disabled={workHoursFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Hours must be a positive number in half-hour increments (e.g. 2, 2.5).
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Committee</Form.Label>
                  <Form.Select
                    value={workHoursFormData.committeeId}
                    onChange={(e) => updateWorkHoursFormData('committeeId', e.target.value)}
                    isInvalid={fieldErrors.includes('workhours:committeeId')}
                    disabled={loadingCommittees || workHoursFormMode === 'disabled'}>
                    <option value="">Select Committee</option>
                    {committees.map((committee) => (
                      <option key={committee.id} value={committee.id}>
                        {committee.codeValue}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Committee is required.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Activity</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={workHoursFormData.activity}
                    onChange={(e) => updateWorkHoursFormData('activity', e.target.value)}
                    isInvalid={fieldErrors.includes('workhours:activity')}
                    placeholder="Description of activity..."
                    maxLength={500}
                    disabled={workHoursFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Activity description is required.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Button
                  variant="success"
                  onClick={saveWorkHours}
                  disabled={workHoursFormMode === 'disabled' || savingWorkHours}
                  className="me-2">
                  {savingWorkHours && <Spinner size="sm" className="me-2" />}
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  Save
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={clearWorkHoursForm}
                  className="me-2"
                  disabled={savingWorkHours}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={enableAddMode}
                  disabled={workHoursFormMode === 'edit' || workHoursFormMode === 'add'}
                  className="me-2">
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add
                </Button>
                {workHoursFormMode === 'edit' && (
                  <Button
                    variant="outline-danger"
                    onClick={() => setShowDeleteWorkHoursModal(true)}
                    disabled={savingWorkHours}>
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Delete
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Work Hours History Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faClock} className="me-2" />
            Work Hours History
          </h5>
        </Card.Header>
        <Card.Body>
          {loadingWorkHours ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <div className="mt-2">Loading work hours...</div>
            </div>
          ) : filteredAndSortedWorkHours.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <FontAwesomeIcon icon={faClock} size="2x" className="mb-3" />
              <div>
                {workHoursYearFilter === 'All'
                  ? 'No work hours recorded yet'
                  : `No work hours recorded for ${workHoursYearFilter}`}
              </div>
              <div className="small">Add work hours using the form above</div>
            </div>
          ) : (
            <>
              {/* Filters Row */}
              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Group className="mb-0">
                    <Form.Label className="me-2 mb-0">Show Hours for Year:</Form.Label>
                    <Form.Select
                      size="sm"
                      value={workHoursYearFilter}
                      onChange={(e) => setWorkHoursYearFilter(e.target.value)}
                      style={{ width: '120px', display: 'inline-block' }}>
                      {availableYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs="auto">
                  <Form.Group className="mb-0">
                    <Form.Label className="me-2 mb-0">Order By:</Form.Label>
                    <Form.Select
                      size="sm"
                      value={workHoursOrderBy}
                      onChange={(e) => setWorkHoursOrderBy(e.target.value as WorkHoursOrderBy)}
                      style={{ width: '200px', display: 'inline-block' }}>
                      <option value="workDate">Work Date (descending)</option>
                      <option value="enteredDate">Entered Date (descending)</option>
                      <option value="updatedDate">Updated Date (descending)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table hover className={styles.workHoursTable}>
                  <thead>
                    <tr>
                      <SortableHeader
                        text="Date"
                        sortField="workHoursDateUtc"
                        sortDirection={workHoursSortDirection}
                        active={workHoursSortField === 'workHoursDateUtc'}
                        onClick={handleWorkHoursSort}
                      />
                      <SortableHeader
                        text="Hours"
                        sortField="workHoursAmount"
                        sortDirection={workHoursSortDirection}
                        active={workHoursSortField === 'workHoursAmount'}
                        onClick={handleWorkHoursSort}
                      />
                      <SortableHeader
                        text="Committee"
                        sortField="committee"
                        sortDirection={workHoursSortDirection}
                        active={workHoursSortField === 'committee'}
                        onClick={handleWorkHoursSort}
                      />
                      <SortableHeader
                        text="Activity"
                        sortField="activity"
                        sortDirection={workHoursSortDirection}
                        active={workHoursSortField === 'activity'}
                        onClick={handleWorkHoursSort}
                      />
                      <SortableHeader
                        text="Entered Date"
                        sortField="dateCreatedUtc"
                        sortDirection={workHoursSortDirection}
                        active={workHoursSortField === 'dateCreatedUtc'}
                        onClick={handleWorkHoursSort}
                      />
                      <SortableHeader
                        text="Entered By"
                        sortField="createdBy"
                        sortDirection={workHoursSortDirection}
                        active={workHoursSortField === 'createdBy'}
                        onClick={handleWorkHoursSort}
                      />
                      <SortableHeader
                        text="Updated Date"
                        sortField="lastModifiedDateUtc"
                        sortDirection={workHoursSortDirection}
                        active={workHoursSortField === 'lastModifiedDateUtc'}
                        onClick={handleWorkHoursSort}
                      />
                      <SortableHeader
                        text="Updated By"
                        sortField="lastModifiedBy"
                        sortDirection={workHoursSortDirection}
                        active={workHoursSortField === 'lastModifiedBy'}
                        onClick={handleWorkHoursSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedWorkHours.map((workHour, index) => {
                      const highlightClass = getRowHighlightClass(workHour);
                      const isSelected = selectedWorkHoursRow === index;
                      return (
                        <tr
                          key={workHour.id}
                          onClick={() => enableEditMode(workHour, index)}
                          className={`${styles.clickableRow} ${isSelected ? styles.selectedRow : highlightClass}`}
                          style={{ cursor: 'pointer' }}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {new Date(workHour.workHoursDateUtc).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </td>
                          <td>{workHour.workHoursAmount.toFixed(2)}</td>
                          <td>{workHour.committee?.codeValue || 'Unknown'}</td>
                          <td>{workHour.activity}</td>
                          <td>{Helpers.toShortDate(workHour.dateCreatedUtc)}</td>
                          <td>{workHour.createdBy}</td>
                          <td>{Helpers.toShortDate(workHour.lastModifiedDateUtc)}</td>
                          <td>{workHour.lastModifiedBy}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                {filteredAndSortedWorkHours.length > 0 && (
                  <div className="mt-3 text-end">
                    <strong>
                      Total Hours:{' '}
                      {filteredAndSortedWorkHours
                        .reduce((sum, wh) => sum + wh.workHoursAmount, 0)
                        .toFixed(2)}
                    </strong>
                  </div>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Legend - Always show below the Work Hours History */}
      <Card className="mt-3">
        <Card.Body>
          <div className={styles.legendItem}>
            <span className={`${styles.legendHighlight} ${styles.legendHighlightYellow}`}>
              Yellow highlight
            </span>
            <span className="ms-2">indicates hours that were entered today</span>
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendHighlight} ${styles.legendHighlightOrange}`}>
              Orange highlight
            </span>
            <span className="ms-2">indicates hours that were updated today</span>
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendHighlight} ${styles.legendHighlightRed}`}>
              Red highlight
            </span>
            <span className="ms-2">indicates work hours date is in the future</span>
          </div>
        </Card.Body>
      </Card>

      <ModalDialog
        showDialog={showDeleteWorkHoursModal}
        heading="Delete Work Hours Entry"
        message={`Are you sure you want to delete this work hours entry (${
          workHoursFormData.workHoursAmount || '0'
        } hours on ${workHoursFormData.workHoursDateUtc})? This cannot be undone from the app.`}
        okCallback={() => {
          setShowDeleteWorkHoursModal(false);
          void deleteWorkHours();
        }}
        okButtonText="Yes, delete entry"
        cancelCallback={() => setShowDeleteWorkHoursModal(false)}
        cancelButtonText="No, keep entry"
      />
    </>
  );
};
